


import { GallerySlide, GallerySlideImage } from '@/utils/api';
import { resizeImageUrl } from '@/utils/helpers/resizeImageUrl';
import { Component, Vue, Prop, Ref } from 'vue-property-decorator';
import throttle from '../../utils/helpers/throttle';
import ResponsiveImage from '../utils/ResponsiveImage/index.vue';
import { createDesktopTabletImage, Dimension } from '../utils/ResponsiveImage/ResponsiveImage.models';
import { ImageGalleryImageBlock, ImageGallerySlide, ImageGallerySlideWithHeadline } from './ImageGallery.models';

const slideHasHeadline = (slide: ImageGallerySlide): slide is ImageGallerySlideWithHeadline => {
  return !!(slide as ImageGallerySlideWithHeadline).headline;
};

const mapImage = (
  { headline, image }: GallerySlideImage,
  tabletDimensions: Dimension,
  desktopDimensions: Dimension
): ImageGalleryImageBlock => {
  return {
    text: headline,
    image: createDesktopTabletImage(resizeImageUrl(image, desktopDimensions), resizeImageUrl(image, tabletDimensions))
  };
};

@Component({
  components: { ResponsiveImage }
})
export default class ImageGallery extends Vue {
  @Ref('gallery') galleryEl!: HTMLElement;

  @Prop({ default: () => [{}] }) public slides!: GallerySlide[];

  public get displayableSlides(): ImageGallerySlide[] {
    return this.slides.map(({ headline, text, images }) => {
      if (headline) {
        const displayableSlide: ImageGallerySlide = {
          headline,
          text,
          blocks: [mapImage(images[0], [226, 117], [600, 338]), mapImage(images[1], [392, 220], [758, 427])]
        };
        return displayableSlide;
      }

      if (images.length === 3) {
        const displayableSlide: ImageGallerySlide = {
          blocks: [
            mapImage(images[0], [310, 174], [600, 338]),
            mapImage(images[1], [310, 174], [600, 338]),
            mapImage(images[2], [372, 209], [758, 427])
          ]
        };
        return displayableSlide;
      }

      if (images.length === 4) {
        const displayableSlide: ImageGallerySlide = {
          blocks: [
            mapImage(images[0], [226, 127], [374, 210]),
            mapImage(images[1], [310, 174], [600, 338]),
            mapImage(images[2], [310, 174], [600, 338]),
            mapImage(images[3], [392, 220], [758, 427])
          ]
        };
        return displayableSlide;
      }

      throw new Error(
        `Encountered a gallery slide with no headline, and less than 3 or more than 4 images - cannot continue.`
      );
    });
  }

  private currentIndex = 0;

  private get firstSlide(): boolean {
    return this.currentIndex === 0;
  }

  private get lastSlide(): boolean {
    return this.currentIndex === this.slides.length - 1;
  }

  private goToPrev() {
    if (!this.firstSlide) {
      this.scrollToIndex(this.currentIndex - 1);
    }
  }

  private goToNext() {
    if (!this.lastSlide) {
      this.scrollToIndex(this.currentIndex + 1);
    }
  }

  private scrollToIndex(index: number) {
    this.galleryEl.scrollTo({
      top: 0,
      left: (this.galleryEl.scrollWidth / this.slides.length) * index,
      behavior: 'smooth'
    });
  }

  private get onScroll() {
    return throttle(() => {
      this.currentIndex = Math.round(this.galleryEl.scrollLeft / (this.galleryEl.scrollWidth / this.slides.length));
    });
  }

  public isIntroSlide(slide: ImageGallerySlideWithHeadline) {
    return slideHasHeadline(slide);
  }

  public is4ImageSlide(slide: ImageGallerySlideWithHeadline) {
    return slide.blocks.length === 4 && !slideHasHeadline(slide);
  }

  public is3ImageSlide(slide: ImageGallerySlideWithHeadline) {
    return slide.blocks.length === 3 && !slideHasHeadline(slide);
  }
}
